import { CommonModule } from '@angular/common';
import { afterNextRender, ChangeDetectorRef, Component, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIconsModule } from '@ng-icons/core';
import { timer } from 'rxjs';

@Component({
  selector: 'app-home',
  imports: [CommonModule, NgIconsModule, ReactiveFormsModule, FormsModule],
  templateUrl: './home.view.html',
  styleUrl: './home.view.scss',
})
export class HomeView {
  scrollY = 0;

  projects = [
    {
      title: 'E-Commerce Platform',
      description: 'A scalable online marketplace with real-time inventory management and seamless checkout experience.',
      tech: ['React', 'Node.js', 'MongoDB'],
      image: 'https://images.unsplash.com/photo-1661956602116-aa6865609028?w=800&q=80',
    },
    {
      title: 'AI Analytics Dashboard',
      description: 'Advanced analytics platform leveraging machine learning for predictive business insights.',
      tech: ['Python', 'TensorFlow', 'React'],
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80',
    },
    {
      title: 'Mobile Banking App',
      description: 'Secure and intuitive mobile banking solution with biometric authentication and instant transfers.',
      tech: ['React Native', 'AWS', 'PostgreSQL'],
      image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&q=80',
    },
    {
      title: 'Healthcare Management System',
      description: 'Comprehensive platform for patient records, appointments, and telemedicine capabilities.',
      tech: ['Vue.js', 'Firebase', 'TypeScript'],
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80',
    },
  ];

  features = [
    {
      icon: 'heroPaintBrush',
      title: 'Custom Development',
      description: 'Tailored solutions built to your exact specifications and business needs',
    },
    {
      icon: 'heroScale',
      title: 'Reliable Solutions',
      description: 'Solutions designed for maximum uptime, scalability, and long-term reliability',
    },
    {
      icon: 'heroUserGroup',
      title: 'Seamless Collaboration',
      description: 'Our team works closely with you at every stage to deliver your desired results',
    },
    {
      icon: 'heroSparkles',
      title: 'Decreased Costs',
      description: 'We help you reduce costs by automating your processes and improving your workflow',
    },
  ];

  about = [
    {
      title: 'Metrics Focused',
      description: 'Analyze your metrics to obtain valuable insights',
      emphasis: 'Insight',
      image: 'images/cash-register.jpg',
    },
    {
      title: 'Customer Experience',
      description: 'Offer personalized experiences to your customers',
      emphasis: 'Engage',
      image: 'images/people.jpg',
    },
    {
      title: 'Company Overview',
      description: 'Keep track of what is happening in your business',
      emphasis: 'Control',
      image: 'images/warehouse.jpg',
    },
  ];
  currentAboutIndex = 0;

  aboutProgress = 0;
  aboutProgressPercentage = '0%';

  stats = [
    { label: 'Cost Savings Achieved', value: '$200K+' },
    { label: 'Ready To Use APIs', value: '25+' },
    { label: 'Analytics Accuracy Increase', value: '95%' },
    { label: 'Optimized Workflows', value: '350+' },
  ];

  portfolio = [
    {
      title: 'Sales Routing',
      description: 'Scale your sale platforms by managing orders from all channels in one place',
      image: 'images/market.jpg',
      websiteUrl: 'https://www.salesrouting.com',
    },
    {
      title: 'Flash Routing',
      description: 'Optimize delivery routes by using real-time data and AI',
      image: 'images/trucks.jpg',
      websiteUrl: 'https://www.flashrouting.com',
    },
    {
      title: 'SET',
      description: 'Launch your own telecomunication service provider without the hassle of outdated systems',
      image: 'images/telecommunications.jpg',
    },
  ];

  contactItems = [
    {
      icon: 'heroAtSymbol',
      label: 'Email',
      value: 'software@ofnos.com',
    },
    {
      icon: 'heroPhone',
      label: 'Phone',
      value: '+52 (33) 4626 3238',
    },
    {
      icon: 'heroMapPin',
      label: 'Location',
      value: 'Guadalajara, Jalisco, Mexico',
    },
  ];

  socials = [
    { icon: 'logos/linkedin.png', label: 'LinkedIn', url: 'https://www.linkedin.com/company/ofnos' },
  ];

  constructor(private cdr: ChangeDetectorRef, private fb: FormBuilder) {
    afterNextRender(() => {
      this._startAboutProgress();
    });
  }

  private _startAboutProgress() {
    this.aboutProgress = 0;
    this.aboutProgressPercentage = '0%';
    timer(0, 500).subscribe(() => {
      this.aboutProgress += 0.1;
      this.aboutProgressPercentage = this.aboutProgress * 100 + '%';
      if (this.aboutProgress >= 1) {
        this.nextAbout();
        this.aboutProgress = 0;
        this.aboutProgressPercentage = '0%';
      }
      this.cdr.detectChanges();
    });
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.scrollY = window.scrollY;
  }

  scrollToSection(id: string) {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }

  nextAbout() {
    this.currentAboutIndex++;
    if (this.currentAboutIndex >= this.about.length) {
      this.currentAboutIndex = 0;
    }
    this.aboutProgress = 0;
    this.aboutProgressPercentage = '0%';
  }

  prevAbout() {
    this.currentAboutIndex--;
    if (this.currentAboutIndex < 0) {
      this.currentAboutIndex = this.about.length - 1;
    }
    this.aboutProgress = 0;
    this.aboutProgressPercentage = '0%';
  }

  setAbout(index: number) {
    if (index >= 0 && index < this.about.length) {
      this.currentAboutIndex = index;
      this.aboutProgress = 0;
      this.aboutProgressPercentage = '0%';
    }
  }

  navigateToWebsite(url: string) {
    window.open(url, '_blank');
  }
}
